import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { updateUserProfileHandle } from "../../services/userProfileService";
import { User } from "../../types/User";
import { debugLogError } from "../../utils/debug-log-error";
import { formatDate } from "../../utils/format-date";
import { formatTimestamp } from "../../utils/format-timestamp";

interface UsernameUpdateFormProps {
  user: User | null;
  userUid: string | null;
  username: string | null;
  onError: (error: Error) => void;
  onClearError: () => void;
}

const UsernameUpdateForm: React.FC<UsernameUpdateFormProps> = ({
  user,
  userUid,
  username,
  onError,
  onClearError,
}) => {
  const [editingUsername, setEditingUsername] = useState(false);
  const [newUsername, setNewUsername] = useState("");

  const handleUsernameEditToggle = useCallback(() => {
    if (
      user?.nextUsernameUpdateAt &&
      user.nextUsernameUpdateAt.toDate() > new Date()
    ) {
      toast.error(
        `You can't update your username right now. Please do so after: ${formatTimestamp(user.nextUsernameUpdateAt)}`
      );
      return;
    }

    setEditingUsername(!editingUsername);
    if (!editingUsername && username) {
      setNewUsername(username);
    }
  }, [user, editingUsername, username]);

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewUsername(event.target.value);
  };

  const handleSaveUsername = useCallback(async () => {
    if (!userUid) {
      return;
    }

    if (!newUsername?.trim()) {
      onError(new Error("Username cannot be empty."));
      return;
    }

    onClearError();
    try {
      if (newUsername === username) {
        // Do nothing;
      } else {
        await updateUserProfileHandle(
          userUid,
          newUsername,
          user?.usernameUpdateCount ?? 0
        );
      }
      setEditingUsername(false);
    } catch (err) {
      debugLogError("ProfilePage::handleSaveUsername", err);
      onError(new Error((err as Error).message));
    }
  }, [user, userUid, username, newUsername, onError, onClearError]);

  const nextUpdateDate = user?.nextUsernameUpdateAt
    ? new Date(user.nextUsernameUpdateAt.toMillis())
    : null;
  const showNextUpdateDate = nextUpdateDate && nextUpdateDate > new Date();

  return (
    <div>
      <div className="form-group username-section">
        <div className="form-label">Username</div>
        {editingUsername ? (
          <>
            <input
              type="text"
              className="form-control"
              value={newUsername}
              onChange={handleUsernameChange}
            />
            <div className="form-actions">
              <button className="btn btn-primary" onClick={handleSaveUsername}>
                Save
              </button>
              <button
                className="btn btn-outline-primary"
                onClick={handleUsernameEditToggle}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                value={username || ""}
                readOnly
              />
              <button
                className="btn btn-outline-dark btn-sm"
                type="button"
                onClick={handleUsernameEditToggle}
                aria-label="Edit username"
              >
                <span className="material-symbols-outlined">edit</span>
              </button>
            </div>
            <div className="form-caption">
              Username updates should happen infrequently.
              {showNextUpdateDate && (
                <>
                  <br />
                  Next update available on{" "}
                  <strong>{formatDate(nextUpdateDate)}</strong>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default UsernameUpdateForm;
